import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { useRequestPosition } from '@features/positions/hooks/useRequestPosition';
import { Close } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import BundledEditor from '@utils/BundledEditor';
import { RequestInfoIcon } from '../../../../vectors/index';
import { getStrippedText } from '@utils/helper';

type RequestPositionModalProps = {
  isOpen: boolean;
  handleClose: () => void;
};

const RequestPositionModal: FC<RequestPositionModalProps> = ({
  isOpen,
  handleClose,
}) => {
  const [errorText, setErrorText] = useState('');
  const [showRequestInfo, setShowRequestInfo] = useState(false);
  const [isPayRange, setIsPayRange] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
  });
  const checkBoxOptions = [
    { name: 'tips', label: 'Tips' },
    { name: 'commission', label: 'Commission' },
    { name: 'bonus', label: 'Bonus' },
    { name: 'incentives', label: 'Incentives' },
    { name: 'benefits', label: 'Benefits' },
  ];
  const handleModalClose = () => {
    reset();
    setShowRequestInfo(false);
    setErrorText('');
    handleClose();
  };

  const { requestPosition, isRequestPositionLoading } = useRequestPosition({
    setErrorText,
  });

  const onSubmit = (data: any) => {
    const htmlRequestBody = `
    <p><strong>Position Name:</strong> ${data.s_title}</p>
    <p><strong>Description:</strong>${data.s_description}</p>
    <p><strong>Payrate:</strong> $${data.s_salary || '0.00'}</p>
    ${data.s_salary_max ? `<p><strong>Payrate Max:</strong> $${data.s_salary_max}</p>` : ''}
    <p><strong>Tips:</strong> ${data.tips ? 'Yes' : 'No'}</p>
    <p><strong>Commission:</strong> ${data.commission ? 'Yes' : 'No'}</p>
    <p><strong>Bonus:</strong> ${data.bonus ? 'Yes' : 'No'}</p>
    <p><strong>Incentives:</strong> ${data.incentives ? 'Yes' : 'No'}</p>
    <p><strong>Benefits:</strong> ${data.benefits ? 'Yes' : 'No'}</p>
  `;

    requestPosition(
      { request_body: htmlRequestBody },
      {
        onSuccess: (data: { status: number }) => {
          if (data.status === 200) {
            handleModalClose();
            toast.success('Your request submitted successfully');
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleModalClose}
      maxWidth={showRequestInfo ? 'sm' : 'xs'}
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleModalClose}
        >
          <Close />
        </IconButton>
        <br />
        {!showRequestInfo && (
          <>
            <RequestInfoIcon />
            <br />
          </>
        )}
        <Typography
          sx={{ marginTop: !showRequestInfo ? '10px' : 'auto' }}
          variant={showRequestInfo ? 'h3' : 'h4'}
        >
          Request a New Position Category
        </Typography>
        <Typography
          sx={{
            fontSize: showRequestInfo ? '14px' : '16px',
            margin: '0 auto',
            marginTop: '10px !important',
          }}
          maxWidth={showRequestInfo ? '400px' : 'auto'}
          textAlign="center"
        >
          {showRequestInfo
            ? 'Tell us about the position you need, and we’ll write a great job description and get it added for you.'
            : ' Remember, you can customize the name of your position category after you select it.'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          {showRequestInfo ? (
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              sx={{
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography
                maxWidth="500px"
                textAlign="center"
                alignSelf="center"
                sx={{ margin: '0 auto' }}
                color="text.secondary"
              >
                We’ll set the status to accepting, and send you an email so you
                can review the information and set it to hiring when you’re
                ready.
              </Typography>

              <TextField
                label="Position Name"
                placeholder="The title of the position you’re hiring for."
                variant="outlined"
                size="small"
                fullWidth
                {...register('s_title', {
                  required: 'Position Name is required',
                  validate: (value) =>
                    getStrippedText(value).length > 0 ||
                    'Position Name cannot be empty',
                })}
                error={!!errors.s_title}
                helperText={
                  typeof errors.s_title?.message === 'string'
                    ? errors.s_title.message
                    : ''
                }
              />

              <Box>
                <Stack
                  mb={2}
                  mt={2}
                  spacing={2}
                  direction={{ sm: 'column', md: 'row' }}
                  alignItems="center"
                >
                  <Typography variant="body2" fontSize="16px">
                    Position Summary
                    <Typography component="span" color="error">
                      *
                    </Typography>
                  </Typography>
                  <Typography color="text.secondary" fontSize="13px">
                    A brief description of the responsibilities of this role in
                    your business.
                  </Typography>
                </Stack>
                <Controller
                  name="s_description"
                  control={control}
                  rules={{
                    required: 'Position Summary is required',
                    validate: (value) =>
                      getStrippedText(value).length > 0 ||
                      'Position Summary cannot be empty',
                  }}
                  render={({ field }) => (
                    <>
                      <BundledEditor
                        value={field.value}
                        onEditorChange={(newValue: string) =>
                          field.onChange(newValue)
                        }
                        init={{
                          height: 200,
                          menubar: false,
                          placeholder:
                            'A brief description of the responsibilities of this role in your business.',
                          plugins: ['lists', 'autolink', 'link', 'pagebreak'],
                          toolbar:
                            'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
                          spellchecker_dialog: true,
                          spellchecker_rpc_url: 'localhost/ephox-spelling',
                          spellchecker_language: 'en_US',
                          spellchecker_active: true,
                          browser_spellcheck: true,
                          contextmenu: false,
                        }}
                      />

                      {Boolean(errors?.s_description?.message) && (
                        <Typography
                          color="error"
                          variant="body2"
                          sx={{ mt: 1 }}
                        >
                          {errors?.s_description?.message?.toString()}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>

              {/* Pay Rate Section */}
              <Typography
                color="text.secondary"
                sx={{ mt: 2, mb: '-7px' }}
                fontSize="13px"
              >
                Add the rate or range, hourly or salary, and any tips or
                incentives.
              </Typography>
              <Stack
                spacing={{ xs: 0, sm: 3 }}
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                sx={{ mb: 3 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography>Pay Rate</Typography>
                  <Tooltip
                    title="Adding pay rates will help your jobs get seen more! Enter dollar amounts for either hourly or annual salary"
                    placement="right"
                    arrow
                  >
                    <HelpOutlineIcon sx={{ fontSize: 18, cursor: 'help' }} />
                  </Tooltip>
                </Stack>

                <FormControlLabel
                  control={<Checkbox checked={isPayRange} />}
                  label="Use a pay range"
                  onChange={() => setIsPayRange(!isPayRange)}
                />

                <Stack direction="row" spacing={2} alignItems="center">
                  <Controller
                    control={control}
                    name="s_salary"
                    rules={{ required: 'Pay rate is required' }}
                    render={({ field }) => (
                      <>
                        <TextField
                          {...field}
                          variant="outlined"
                          type="number"
                          size="small"
                          fullWidth
                          sx={{ maxWidth: '170px' }}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? e.target.value : null,
                            )
                          }
                          placeholder="0.00"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                        {Boolean(errors?.s_salary?.message) && (
                          <Typography
                            color="error"
                            variant="body2"
                            sx={{ mt: 1 }}
                          >
                            {errors?.s_salary?.message?.toString()}
                          </Typography>
                        )}
                      </>
                    )}
                  />

                  {isPayRange && (
                    <>
                      <Typography>-</Typography>
                      <Controller
                        control={control}
                        name="s_salary_max"
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              variant="outlined"
                              size="small"
                              type="number"
                              fullWidth
                              sx={{ maxWidth: '170px' }}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value ? e.target.value : null,
                                )
                              }
                              placeholder="0.00"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AttachMoneyIcon />
                                  </InputAdornment>
                                ),
                              }}
                            />
                            {Boolean(errors?.s_salary_max?.message) && (
                              <Typography
                                color="error"
                                variant="body2"
                                sx={{ mt: 1 }}
                              >
                                {errors?.s_salary_max?.message?.toString()}
                              </Typography>
                            )}
                          </>
                        )}
                      />
                    </>
                  )}
                </Stack>
              </Stack>

              {/* Additional Compensation Checkboxes */}
              <Grid container spacing={1} justifyContent="space-between">
                {checkBoxOptions.map((option) => (
                  <Grid item xs={6} sm="auto" key={option.name}>
                    <Controller
                      control={control}
                      name={option.name}
                      defaultValue={false}
                      render={({ field: { value, onChange } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!value}
                              onChange={(e) => onChange(e.target.checked)}
                            />
                          }
                          label={option.label}
                          sx={{ width: '100%' }}
                        />
                      )}
                    />
                  </Grid>
                ))}
              </Grid>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isRequestPositionLoading}
                disabled={!isValid}
              >
                Submit Request
              </LoadingButton>

              <Button
                variant="outlined"
                color="error"
                disabled={isRequestPositionLoading}
                onClick={handleModalClose}
              >
                Cancel
              </Button>
            </Box>
          ) : (
            <>
              <Typography textAlign="center" color="text.secondary">
                Example: The Chef category can be named “Executive Chef” or
                “Lead Chef”. Similarly, Crew Member can be named “Front of
                House” or “Counter Service”.
              </Typography>
              <Button variant="contained" onClick={handleClose}>
                Let me Check Again
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowRequestInfo(true);
                }}
              >
                Request a New Position Category
              </Button>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default RequestPositionModal;
